/**
 * Analytics API service
 * Phase 1: Foundation & Core Analytics
 */

import { apiClient, handleApiError } from './api';

export interface AnalyticsOverview {
  total_workshops: number;
  completed_workshops: number;
  ongoing_workshops: number;
  planned_workshops: number;
  passive_workshops: number;
  completion_rate: number;
  status_counts: Record<string, number>;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  overdue_workshops: number;
  upcoming_deadlines: number;
  new_workshops_30d: number;
  completions_30d: number;
  // Chart data
  completion_rates?: Array<{
    month: string;
    completion_rate: number;
    target_rate: number;
  }>;
  status_distribution?: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  progress_velocity?: Array<{
    month: string;
    velocity: number;
    target_velocity: number;
    workshops_completed: number;
  }>;
  savings_trends?: Array<{
    month: string;
    estimated_savings: number;
    released_savings: number;
    cumulative_estimated: number;
    cumulative_released: number;
  }>;
  department_performance?: Array<{
    department: string;
    workshops_count: number;
    completion_rate: number;
    avg_progress: number;
  }>;
  // Performance tab chart data
  progress_distribution?: Record<string, number>;
  performance_by_status?: Array<{
    status: string;
    count: number;
    average_progress: number;
  }>;
  velocity_trend?: Array<{
    month: string;
    velocity: number;
    target_velocity: number;
    workshops_completed: number;
  }>;
  risk_distribution?: Array<{
    risk_level: string;
    count: number;
    percentage: number;
  }>;
  department_performance_scatter?: Array<{
    department: string;
    workshop_count: number;
    average_progress: number;
    completion_rate: number;
  }>;
  category_performance?: Array<{
    category: string;
    average_progress: number;
    workshop_count: number;
    completion_rate: number;
  }>;
  // Financial tab chart data
  savings_trend?: Array<{
    month: string;
    estimated_savings: number;
    released_savings: number;
    cumulative_estimated: number;
    cumulative_released: number;
  }>;
  roi_analysis?: Array<{
    name: string;
    roi: number;
    total_investment: number;
    total_return: number;
    workshop_count: number;
  }>;
  savings_distribution?: Array<{
    category: string;
    estimated_savings: number;
    released_savings: number;
    percentage: number;
  }>;
  financial_performance_status?: Array<{
    status: string;
    workshop_count: number;
    avg_estimated_savings: number;
    avg_released_savings: number;
    total_estimated: number;
    total_released: number;
    realization_rate: number;
  }>;
  cost_benefit_analysis?: Array<{
    workshop_id: number;
    workshop_title: string;
    investment_cost: number;
    estimated_savings: number;
    released_savings: number;
    roi: number;
    status: string;
  }>;
  monthly_savings_forecast?: Array<{
    month: string;
    historical_estimated: number;
    historical_released: number;
    forecast_estimated: number;
    forecast_released: number;
    confidence_level: number;
  }>;
}

export interface WorkshopPerformance {
  average_progress: number;
  progress_distribution: Record<string, number>;
  average_completion_time: number;
  velocity_trend: Array<{
    month: string;
    new_workshops: number;
    completed_workshops: number;
    velocity: number;
  }>;
  performance_by_status: Record<string, {
    count: number;
    average_progress: number;
  }>;
  performance_by_category: Record<string, {
    count: number;
    average_progress: number;
  }>;
  performance_by_department: Record<string, {
    count: number;
    average_progress: number;
  }>;
}

export interface FinancialAnalytics {
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  monthly_savings_trend: Array<{
    month: string;
    estimated_savings: number;
    released_savings: number;
    realization_rate: number;
  }>;
  savings_by_cc_category: Record<string, {
    estimated: number;
    released: number;
    realization_rate: number;
  }>;
  savings_by_sqdcm_category: Record<string, {
    estimated: number;
    released: number;
    realization_rate: number;
  }>;
  savings_by_workstream: Record<string, {
    estimated: number;
    released: number;
    realization_rate: number;
  }>;
  average_roi: number;
  top_performing_workshops: Array<{
    workshop_number: number;
    title: string;
    estimated_savings: number;
    released_savings: number;
    roi: number;
    progress: number;
  }>;
}

export interface AnalyticsFilters {
  start_date?: string;
  end_date?: string;
  departments?: string[];
  plants?: string[];
  statuses?: string[];
  min_progress?: number;
  max_progress?: number;
  min_savings?: number;
  max_savings?: number;
  mentors?: string[];
  period?: '7d' | '30d' | '90d' | '1y' | 'all';
  overdue_only?: boolean;
  high_risk_only?: boolean;
  categories?: string[];
}

// Phase 2: Team & Department Analytics Interfaces

export interface MentorPerformance {
  mentor_id: number;
  mentor_name: string;
  total_workshops: number;
  completed_workshops: number;
  success_rate: number;
  average_progress: number;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  active_mentees: number;
  workload_score: number;
  performance_trend?: Array<{
    period: string;
    success_rate: number;
    workshops_completed: number;
  }>;
}

export interface TeamCollaboration {
  cross_department_participation: number;
  average_team_size: number;
  collaboration_score: number;
  mentee_development_rate: number;
  knowledge_sharing_index: number;
  team_diversity_score: number;
}

export interface DepartmentBenchmark {
  department_id: number;
  department_name: string;
  total_workshops: number;
  completion_rate: number;
  average_progress: number;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  resource_utilization: number;
  goal_achievement_rate: number;
  performance_rank: number;
  improvement_trend: number;
}

export interface PlantAnalytics {
  plant_id: number;
  plant_name: string;
  location: string;
  total_workshops: number;
  completion_rate: number;
  average_progress: number;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  performance_score: number;
  best_practices_count: number;
  regional_rank: number;
}

export interface CrossDepartmentInsights {
  cross_department_workshops: number;
  department_collaboration: Record<string, {
    workshop_count: number;
    mentors: string[];
    collaboration_score: number;
  }>;
  total_departments_involved: number;
}

export interface PerformanceInsight {
  type: 'success' | 'warning' | 'alert' | 'info';
  title: string;
  message: string;
  recommendation: string;
}

export interface TeamPerformance {
  mentor_performance: MentorPerformance[];
  team_collaboration: TeamCollaboration;
  department_benchmarking: DepartmentBenchmark[];
  plant_analytics: PlantAnalytics[];
  cross_department_insights: CrossDepartmentInsights;
  performance_insights: PerformanceInsight[];
}

// Phase 3: Advanced Analytics & Trends Interfaces

export interface TrendData {
  period: string;
  value: number;
  change_percentage: number;
  trend_direction: 'up' | 'down' | 'stable';
}

export interface TemporalAnalytics {
  monthly_trends: TrendData[];
  quarterly_trends: TrendData[];
  yearly_comparison: {
    current_year: {
      workshops: number;
      avg_progress: number;
    };
    previous_year: {
      workshops: number;
      avg_progress: number;
    };
    growth_rate: number;
  };
  seasonal_patterns: Record<string, number>;
  velocity_trends: TrendData[];
  completion_trends: TrendData[];
  savings_trends: TrendData[];
}

export interface RiskAssessment {
  workshop_id: number;
  workshop_number: string;
  title: string;
  risk_score: number;
  risk_factors: Array<{
    factor: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }>;
  success_probability: number;
  recommended_actions: string[];
}

export interface PredictiveAnalytics {
  risk_assessments: RiskAssessment[];
  high_risk_workshops: number;
  medium_risk_workshops: number;
  low_risk_workshops: number;
  overall_success_probability: number;
  resource_optimization_suggestions: Array<{
    type: string;
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    recommendation: string;
  }>;
}

export interface SQDCMCategory {
  category: string;
  total_workshops: number;
  completed_workshops: number;
  completion_rate: number;
  average_progress: number;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  performance_score: number;
  improvement_trend: number;
}

export interface SQDCMAnalytics {
  safety_performance: SQDCMCategory;
  quality_performance: SQDCMCategory;
  delivery_performance: SQDCMCategory;
  cost_performance: SQDCMCategory;
  morale_performance: SQDCMCategory;
  overall_sqdcm_score: number;
  category_rankings: Array<{
    rank: number;
    category: string;
    performance_score: number;
    completion_rate: number;
    realization_rate: number;
  }>;
}

export interface APSMaturityLevel {
  level: string;
  workshops_count: number;
  completion_rate: number;
  average_progress: number;
  skill_development_score: number;
  knowledge_transfer_rate: number;
}

export interface APSMaturity {
  current_maturity_distribution: APSMaturityLevel[];
  progression_tracking: Array<{
    period: string;
    beginner_count: number;
    intermediate_count: number;
    advanced_count: number;
    expert_count: number;
  }>;
  skill_development_metrics: {
    total_skills_developed: number;
    average_skill_level: number;
    skill_development_rate: number;
    top_skills: string[];
  };
  knowledge_transfer_metrics: {
    knowledge_sessions_conducted: number;
    knowledge_retention_rate: number;
    cross_team_learning: number;
    mentor_effectiveness: number;
  };
  maturity_improvement_rate: number;
  recommended_learning_paths: Array<{
    path_name: string;
    target_audience: string;
    duration: string;
    skills_covered: string[];
    completion_rate: number;
  }>;
}

class AnalyticsService {
  /**
   * Get comprehensive analytics overview
   */
  async getOverview(filters?: AnalyticsFilters): Promise<AnalyticsOverview> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/overview/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop performance analytics
   */
  async getWorkshopPerformance(filters?: AnalyticsFilters): Promise<WorkshopPerformance> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/workshops/performance/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get financial analytics
   */
  async getFinancialAnalytics(filters?: AnalyticsFilters): Promise<FinancialAnalytics> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/financial/summary/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get all analytics data in one call
   */
  async getAllAnalytics(filters?: AnalyticsFilters): Promise<{
    overview: AnalyticsOverview;
    performance: WorkshopPerformance;
    financial: FinancialAnalytics;
  }> {
    try {
      const [overview, performance, financial] = await Promise.all([
        this.getOverview(filters),
        this.getWorkshopPerformance(filters),
        this.getFinancialAnalytics(filters)
      ]);

      return { overview, performance, financial };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  // Phase 2: Team & Department Analytics Methods

  /**
   * Get comprehensive team performance analytics
   */
  async getTeamPerformance(filters?: AnalyticsFilters): Promise<TeamPerformance> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/teams/performance/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get mentor performance data only
   */
  async getMentorPerformance(filters?: AnalyticsFilters): Promise<MentorPerformance[]> {
    try {
      const teamData = await this.getTeamPerformance(filters);
      return teamData.mentor_performance;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get department benchmarking data only
   */
  async getDepartmentBenchmarking(filters?: AnalyticsFilters): Promise<DepartmentBenchmark[]> {
    try {
      const teamData = await this.getTeamPerformance(filters);
      return teamData.department_benchmarking;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get plant analytics data only
   */
  async getPlantAnalytics(filters?: AnalyticsFilters): Promise<PlantAnalytics[]> {
    try {
      const teamData = await this.getTeamPerformance(filters);
      return teamData.plant_analytics;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get team collaboration metrics only
   */
  async getTeamCollaboration(filters?: AnalyticsFilters): Promise<TeamCollaboration> {
    try {
      const teamData = await this.getTeamPerformance(filters);
      return teamData.team_collaboration;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get performance insights and recommendations
   */
  async getPerformanceInsights(filters?: AnalyticsFilters): Promise<PerformanceInsight[]> {
    try {
      const teamData = await this.getTeamPerformance(filters);
      return teamData.performance_insights;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  // Phase 3: Advanced Analytics & Trends Methods

  /**
   * Get temporal analytics and trends
   */
  async getTemporalAnalytics(filters?: AnalyticsFilters): Promise<TemporalAnalytics> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/trends/temporal/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get predictive analytics and risk assessment
   */
  async getPredictiveAnalytics(filters?: AnalyticsFilters): Promise<PredictiveAnalytics> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/predictive/risk-assessment/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get SQDCM category performance analytics
   */
  async getSQDCMAnalytics(filters?: AnalyticsFilters): Promise<SQDCMAnalytics> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/categories/sqdcm/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get APS maturity progression analytics
   */
  async getAPSMaturity(filters?: AnalyticsFilters): Promise<APSMaturity> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/maturity/aps-progression/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  // Phase 4: Interactive Features & Visualization Methods

  /**
   * Get advanced filtering data
   */
  async getAdvancedFiltering(filters?: any): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/filtering/advanced/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get search suggestions
   */
  async getSearchSuggestions(query: string): Promise<any[]> {
    try {
      const response = await apiClient.get(`/analytics/search/suggestions/?q=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get quick insights
   */
  async getQuickInsights(filters?: any): Promise<any[]> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => params.append(key, item.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await apiClient.get(`/analytics/insights/quick/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create export job
   */
  async createExport(exportData: any): Promise<any> {
    try {
      const response = await apiClient.post('/analytics/export/', exportData);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get export jobs
   */
  async getExportJobs(): Promise<any[]> {
    try {
      const response = await apiClient.get('/analytics/export/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get report templates
   */
  async getReportTemplates(): Promise<any[]> {
    try {
      const response = await apiClient.get('/analytics/reports/templates/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get scheduled reports
   */
  async getScheduledReports(): Promise<any[]> {
    try {
      const response = await apiClient.get('/analytics/reports/scheduled/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Save dashboard layout
   */
  async saveDashboardLayout(layout: any): Promise<any> {
    try {
      const response = await apiClient.post('/analytics/snapshots/', layout);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get dashboard layout
   */
  async getDashboardLayout(): Promise<any> {
    try {
      const response = await apiClient.get('/analytics/snapshots/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get department analytics charts data
   */
  async getDepartmentCharts(filters?: AnalyticsFilters): Promise<DepartmentChartsData> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.period) params.append('period', filters.period);
        if (filters.departments && filters.departments.length > 0) {
          filters.departments.forEach(dept => params.append('departments', dept));
        }
        if (filters.plants && filters.plants.length > 0) {
          filters.plants.forEach(plant => params.append('plants', plant));
        }
      }
      
      const response = await apiClient.get(`/analytics/departments/charts/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get team analytics charts data
   */
  async getTeamCharts(filters?: AnalyticsFilters): Promise<TeamChartsData> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.period) params.append('period', filters.period);
        if (filters.departments && filters.departments.length > 0) {
          filters.departments.forEach(dept => params.append('departments', dept));
        }
        if (filters.plants && filters.plants.length > 0) {
          filters.plants.forEach(plant => params.append('plants', plant));
        }
      }
      
      const response = await apiClient.get(`/analytics/teams/charts/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get plant analytics charts data
   */
  async getPlantCharts(filters?: AnalyticsFilters): Promise<PlantChartsData> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.period) params.append('period', filters.period);
        if (filters.departments && filters.departments.length > 0) {
          filters.departments.forEach(dept => params.append('departments', dept));
        }
        if (filters.plants && filters.plants.length > 0) {
          filters.plants.forEach(plant => params.append('plants', plant));
        }
      }
      
      const response = await apiClient.get(`/analytics/plants/charts/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getInsightsCharts(filters?: AnalyticsFilters): Promise<InsightsChartsData> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.period) params.append('period', filters.period);
        if (filters.departments && filters.departments.length > 0) {
          filters.departments.forEach(dept => params.append('departments', dept));
        }
        if (filters.plants && filters.plants.length > 0) {
          filters.plants.forEach(plant => params.append('plants', plant));
        }
      }
      
      const response = await apiClient.get(`/analytics/insights/charts/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }
}

// Department Charts Data Interfaces
export interface DepartmentChartsData {
  performance_comparison: Array<{
    department: string;
    workshop_count: number;
    completion_rate: number;
    avg_progress: number;
    total_savings: number;
  }>;
  savings_analysis: Array<{
    department: string;
    estimated_savings: number;
    released_savings: number;
    realization_rate: number;
    workshop_count: number;
  }>;
  progress_trends: {
    data: Array<{
      month: string;
      [key: string]: string | number;
    }>;
    departments: string[];
  };
  workshop_distribution: Array<{
    department: string;
    workshop_count: number;
    percentage: number;
    completion_rate: number;
  }>;
  risk_assessment: Array<{
    department: string;
    overdue_workshops: number;
    low_progress: number;
    high_risk: number;
    delayed_deadlines: number;
    resource_shortage: number;
    quality_issues: number;
  }>;
  mentor_performance: Array<{
    department: string;
    mentor_count: number;
    success_rate: number;
    avg_workshop_duration: number;
    mentee_satisfaction: number;
    workshop_count: number;
  }>;
}

// Team Charts Data Interfaces
export interface TeamChartsData {
  performance_comparison: Array<{
    team_name: string;
    success_rate: number;
    collaboration_score: number;
    knowledge_sharing_index: number;
    mentee_development_rate: number;
    workshop_count: number;
    performance_score: number;
  }>;
  mentor_effectiveness: Array<{
    mentor_name: string;
    workload_score: number;
    success_rate: number;
    mentee_satisfaction: number;
    workshop_count: number;
    active_mentees: number;
    effectiveness_score: number;
  }>;
  performance_trends: {
    data: Array<{
      month: string;
      [key: string]: string | number;
    }>;
    teams: string[];
  };
  workload_distribution: Array<{
    team_name: string;
    workshop_count: number;
    percentage: number;
    workload_score: number;
    active_mentors: number;
  }>;
  knowledge_sharing_matrix: {
    data: Array<{
      source_team: string;
      target_team: string;
      knowledge_flow: number;
      collaboration_frequency: number;
      shared_resources: number;
      cross_team_projects: number;
    }>;
    teams: string[];
  };
  collaboration_network: Array<{
    team_name: string;
    collaboration_score: number;
    network_centrality: number;
    knowledge_hub_score: number;
    cross_team_connections: number;
    influence_score: number;
    team_size: number;
  }>;
}

// Plant Charts Data Interfaces
export interface PlantChartsData {
  performance_comparison: Array<{
    plant_name: string;
    success_rate: number;
    completion_rate: number;
    avg_progress: number;
    workshop_count: number;
    total_savings: number;
    performance_score: number;
  }>;
  savings_analysis: Array<{
    plant_name: string;
    estimated_savings: number;
    released_savings: number;
    realization_rate: number;
    roi: number;
    cost_benefit_ratio: number;
    workshop_count: number;
  }>;
  progress_trends: {
    data: Array<{
      month: string;
      [key: string]: string | number;
    }>;
    plants: string[];
  };
  workshop_distribution: Array<{
    plant_name: string;
    workshop_count: number;
    percentage: number;
    completion_rate: number;
    active_mentors: number;
  }>;
  risk_assessment: Array<{
    plant_name: string;
    overdue_workshops: number;
    low_progress: number;
    high_risk: number;
    delayed_deadlines: number;
    resource_shortage: number;
    quality_issues: number;
  }>;
  mentor_performance: Array<{
    plant_name: string;
    mentor_count: number;
    success_rate: number;
    mentee_satisfaction: number;
    avg_workshop_duration: number;
    workshop_count: number;
    performance_score: number;
  }>;
}

// Insights Charts Data Interfaces
export interface InsightsChartsData {
  strategic_performance: {
    kpis: {
      total_workshops: number;
      completion_rate: number;
      total_savings: number;
      active_mentors: number;
      success_rate: number;
      avg_progress: number;
    };
    trends: Array<{
      month: string;
      workshops: number;
      completions: number;
      savings: number;
      mentors: number;
    }>;
    performance_metrics: {
      efficiency_score: number;
      quality_score: number;
      innovation_score: number;
      collaboration_score: number;
    };
  };
  collaboration_matrix: {
    matrix: Array<{
      source_department: string;
      target_department: string;
      collaboration_score: number;
      joint_workshops: number;
      knowledge_sharing: number;
      cross_training: number;
    }>;
    departments: string[];
    insights: {
      top_collaborators: Array<{
        department: string;
        collaboration_score: number;
        partnerships: number;
      }>;
      silo_breakers: Array<{
        department: string;
        cross_department_initiatives: number;
        impact_score: number;
      }>;
      collaboration_gaps: Array<{
        department_pair: string;
        potential_score: number;
        opportunity: string;
      }>;
    };
  };
  predictive_analytics: {
    success_probability: Array<{
      workshop_id: string;
      title: string;
      current_progress: number;
      success_probability: number;
      risk_factors: string[];
      completion_forecast: string;
    }>;
    resource_forecast: Array<{
      month: string;
      mentor_demand: number;
      mentor_availability: number;
      workshop_capacity: number;
      resource_gap: number;
    }>;
    performance_trends: Array<{
      month: string;
      actual_completion: number;
      predicted_completion: number;
      confidence_interval_upper: number;
      confidence_interval_lower: number;
    }>;
    risk_assessment: {
      high_risk_workshops: number;
      medium_risk_workshops: number;
      low_risk_workshops: number;
      critical_issues: Array<{
        issue: string;
        impact: number;
        probability: number;
        mitigation: string;
      }>;
    };
    recommendations: Array<{
      category: string;
      priority: 'high' | 'medium' | 'low';
      recommendation: string;
      expected_impact: number;
      implementation_effort: 'low' | 'medium' | 'high';
    }>;
  };
  roi_business_impact: {
    investment_flow: {
      total_investment: number;
      training_costs: number;
      mentor_costs: number;
      infrastructure_costs: number;
      operational_costs: number;
    };
    business_impact: {
      direct_savings: number;
      indirect_savings: number;
      productivity_gains: number;
      quality_improvements: number;
      innovation_value: number;
      total_business_value: number;
    };
    roi_metrics: {
      roi_percentage: number;
      payback_period_months: number;
      net_present_value: number;
      cost_benefit_ratio: number;
      break_even_point: string;
    };
    department_roi: Array<{
      department: string;
      investment: number;
      returns: number;
      roi_percentage: number;
      payback_period: number;
    }>;
    timeline_analysis: Array<{
      month: string;
      cumulative_investment: number;
      cumulative_returns: number;
      net_value: number;
      roi_trend: number;
    }>;
    value_attribution: Array<{
      category: string;
      value: number;
      percentage: number;
      description: string;
    }>;
  };
  benchmarking_matrix: {
    benchmarking_matrix: Array<{
      entity_name: string;
      entity_type: 'department' | 'team' | 'plant';
      efficiency_score: number;
      quality_score: number;
      speed_score: number;
      innovation_score: number;
      collaboration_score: number;
      overall_performance: number;
      workshop_count: number;
      success_rate: number;
    }>;
    performance_dimensions: {
      efficiency: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      quality: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      speed: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      innovation: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      collaboration: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
    };
    best_practices: Array<{
      practice: string;
      top_performers: string[];
      impact_score: number;
      implementation_difficulty: 'low' | 'medium' | 'high';
      description: string;
    }>;
    improvement_opportunities: Array<{
      entity: string;
      dimension: string;
      current_score: number;
      potential_score: number;
      improvement_potential: number;
      recommendations: string[];
    }>;
  };
  recommendations_engine: {
    ai_insights: Array<{
      category: string;
      insight: string;
      confidence: number;
      impact_score: number;
      urgency: 'low' | 'medium' | 'high' | 'critical';
      supporting_data: string[];
    }>;
    strategic_recommendations: Array<{
      recommendation: string;
      category: 'efficiency' | 'quality' | 'innovation' | 'collaboration' | 'risk_mitigation';
      priority: 'low' | 'medium' | 'high' | 'critical';
      expected_impact: number;
      implementation_effort: 'low' | 'medium' | 'high';
      timeline: string;
      success_probability: number;
      dependencies: string[];
      metrics_to_track: string[];
    }>;
    action_plans: Array<{
      plan_name: string;
      description: string;
      steps: Array<{
        step: string;
        duration: string;
        responsible: string;
        success_criteria: string;
      }>;
      expected_outcomes: string[];
      risk_factors: string[];
      mitigation_strategies: string[];
    }>;
    performance_gaps: Array<{
      gap_area: string;
      current_performance: number;
      target_performance: number;
      gap_size: number;
      root_causes: string[];
      improvement_opportunities: string[];
    }>;
    resource_optimization: {
      underutilized_resources: Array<{
        resource: string;
        utilization_rate: number;
        optimization_potential: number;
        recommendations: string[];
      }>;
      overutilized_resources: Array<{
        resource: string;
        utilization_rate: number;
        risk_level: 'low' | 'medium' | 'high';
        mitigation_strategies: string[];
      }>;
      resource_reallocation_opportunities: Array<{
        from_resource: string;
        to_resource: string;
        potential_benefit: number;
        implementation_complexity: 'low' | 'medium' | 'high';
      }>;
    };
  };
}

export const analyticsService = new AnalyticsService();
